import React from 'react';
import { Image as RNImage, StyleSheet, TextInput, View } from 'react-native';

import { createStringValidator, useAppForm } from '@components/Form';
import Icon from '@components/Icon';
import ScreenContainer from '@components/ScreenContainer';
import Switch from '@components/Switch';
import { TextInputIcon } from '@components/TextInput';
import { Caption, IconTitle, Paragraph, PrimaryHeadline } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import { useCurrentUser } from '@context/UsersContext';
import { validatePlace } from '@db/Places';
import {
  joinUserDisplayName,
  splitUserDisplayName,
  validateUserDisplayName,
  validateUsername,
} from '@db/Users';
import { useCreateUserMutation, useUpdateUserMutation } from '@db/Users/Queries';
import { errorAlert } from '@helpers/Alert';
import { logError } from '@helpers/Loggers';
import { auth } from '@helpers/firebase/Initialize';
import { OnboardingRoute } from '@navigation/Routes';
import { CreateUserScreenProps, UpdateUserScreenProps } from '@navigation/Types';
import { Photo } from '@types';
import { Place } from '@types';

const USERNAME_VALIDATION_DEBOUNCE_MS = 500;
export function CreateUserScreen({ navigation }: CreateUserScreenProps) {
  const createUserMutation = useCreateUserMutation();
  const onPress = async (
    displayName: string,
    username: string,
    hometown: Place,
    isPublic: boolean,
    profilePhoto?: Photo
  ) => {
    await createUserMutation.mutateAsyncOrThrow({
      user: {
        displayName,
        username,
        hometown,
        public: isPublic,
      },
      profilePhoto,
    });
    navigation.navigate(OnboardingRoute.UserPrivateForm);
  };
  return <UserFormScreen onPress={onPress} loadExistingUser={false} title="Create your profile" />;
}

export function UpdateUserScreen({ navigation }: UpdateUserScreenProps) {
  const updateUserMutation = useUpdateUserMutation();
  const onPress = async (
    displayName: string,
    username: string,
    hometown: Place,
    isPublic: boolean,
    profilePhoto?: Photo
  ) => {
    await updateUserMutation.mutateAsyncOrThrow({
      displayName,
      hometown,
      isPublic,
      profilePhoto,
      username,
    });
    navigation.goBack();
  };
  return <UserFormScreen onPress={onPress} loadExistingUser buttonText="Update" />;
}

type UserFormScreenProps = {
  onPress: (
    displayName: string,
    username: string,
    hometown: Place,
    isPublic: boolean,
    profilePhoto?: Photo
  ) => Promise<void>;
  loadExistingUser: boolean;
  title?: string;
  screenContainerProps?: React.ComponentProps<typeof ScreenContainer>;
  buttonText?: string;
};

type UserFormValues = {
  firstName: string;
  hometown: Place | undefined;
  isPublic: boolean;
  lastName: string;
  profilePhoto: Photo | null;
  username: string;
};

type UsernameAvailabilityStatus = 'idle' | 'checking' | 'available' | 'unavailable';

type UsernameAvailability = {
  username: string;
  status: UsernameAvailabilityStatus;
};

export default function UserFormScreen({
  onPress,
  loadExistingUser,
  title,
  screenContainerProps,
  buttonText,
}: UserFormScreenProps) {
  const { userDoc } = useCurrentUser();
  const appTheme = useAppTheme();
  const [initialAuthDisplayName] = React.useState(
    () => auth.currentUser?.displayName?.trim() ?? ''
  );
  // addUser can update auth.currentUser.displayName before navigation finishes.
  // Keep this mount-time decision stable so create-mode name fields do not flicker away.
  const showDisplayNameFields = loadExistingUser || !initialAuthDisplayName;

  const loading = loadExistingUser && !userDoc;
  const [usernameAvailability, setUsernameAvailability] = React.useState<UsernameAvailability>({
    username: '',
    status: 'idle',
  });
  const usernameValidationId = React.useRef(0);
  const lastNameRef = React.useRef<TextInput>(null);
  const usernameRef = React.useRef<TextInput>(null);
  const hometownRef = React.useRef<TextInput>(null);
  const displayNameValidators = {
    onChange: createStringValidator(validateUserDisplayName),
    onSubmit: createStringValidator(validateUserDisplayName),
  };

  const maybeValidateUsername = async (
    username: string,
    options: { updateAvailability?: boolean } = {}
  ) => {
    const validationId = options.updateAvailability ? usernameValidationId.current + 1 : undefined;
    if (validationId) {
      usernameValidationId.current = validationId;
      setUsernameAvailability({
        username,
        status: username ? 'checking' : 'idle',
      });
    }

    // if user owns the current username, the validator will return an error
    // because the username will be "taken". we detect this and avoid
    // any validation if this is the case.
    let usernameError: string = '';
    const userOwnsUsername = userDoc && userDoc.username === username;
    if (!userOwnsUsername) {
      usernameError = await validateUsername(username);
    }
    if (validationId && usernameValidationId.current === validationId) {
      setUsernameAvailability({
        username,
        status: !username ? 'idle' : usernameError ? 'unavailable' : 'available',
      });
    }
    return usernameError;
  };

  const form = useAppForm({
    defaultValues: {
      firstName: '',
      hometown: undefined,
      isPublic: true,
      lastName: '',
      profilePhoto: null,
      username: '',
    } as UserFormValues,
    onSubmit: async ({ value }) => {
      if (!value.hometown) {
        throw new Error('User form submitted without a hometown.');
      }

      const displayName = showDisplayNameFields
        ? joinUserDisplayName(value)
        : initialAuthDisplayName;
      if (!displayName) {
        throw new Error('User form submitted without a display name.');
      }

      try {
        await onPress(
          displayName,
          value.username,
          value.hometown,
          value.isPublic,
          value.profilePhoto ?? undefined
        );
      } catch (error) {
        errorAlert(error);
        throw error;
      }
    },
  });

  React.useEffect(() => {
    try {
      if (userDoc && loadExistingUser) {
        const displayNameParts = splitUserDisplayName(userDoc.displayName);
        form.setFieldValue('firstName', displayNameParts.firstName);
        form.setFieldValue('lastName', displayNameParts.lastName);
        form.setFieldValue('username', userDoc.username);
        form.setFieldValue('hometown', userDoc.hometown);
        form.setFieldValue('isPublic', userDoc.public);
        if (userDoc.photoUrl) {
          RNImage.getSize(
            userDoc.photoUrl,
            (width, height) => {
              form.setFieldValue('profilePhoto', { uri: userDoc.photoUrl, width, height });
            },
            (error) => logError(error)
          );
        }
      }
    } catch (error) {
      logError(error);
    }
  }, [form, loadExistingUser, userDoc]);

  const renderUsernameAvailabilityIcon = (username: string) => {
    if (!username || usernameAvailability.username !== username) {
      return undefined;
    }

    switch (usernameAvailability.status) {
      case 'checking':
        return (
          <TextInputIcon
            icon="progress-clock"
            loading
            testID="screens.UserFormScreen.TextInput.Username.Icon.Checking"
          />
        );
      case 'available':
        return (
          <TextInputIcon
            icon="check-circle"
            color={appTheme.colors.primary}
            testID="screens.UserFormScreen.TextInput.Username.Icon.Available"
          />
        );
      case 'unavailable':
        return (
          <TextInputIcon
            icon="close-circle"
            color={appTheme.colors.error}
            testID="screens.UserFormScreen.TextInput.Username.Icon.Unavailable"
          />
        );
      default:
        return undefined;
    }
  };

  const renderHeader = (
    icon: React.ComponentProps<typeof IconTitle>['icon'],
    title: string,
    caption?: string
  ) => (
    <View>
      <IconTitle icon={icon} text={title} />
      {caption ? <Caption style={styles.caption}>{caption}</Caption> : null}
    </View>
  );

  const renderDisplayNameInputs = () =>
    showDisplayNameFields ? (
      <View style={styles.itemContainer}>
        {renderHeader(
          'card-account-details-outline',
          'Name',
          'Your name helps friends connect with you.'
        )}
        <form.AppField name="firstName" validators={displayNameValidators}>
          {(field) => (
            <field.TextInput
              placeholder="Enter your first name"
              returnKeyType="next"
              autoCorrect={false}
              autoComplete="given-name"
              textContentType="givenName"
              onSubmitEditing={() => lastNameRef.current?.focus()}
              containerStyle={styles.textInputContainer}
              testID="screens.UserFormScreen.TextInput.FirstName"
            />
          )}
        </form.AppField>
        <form.AppField name="lastName" validators={displayNameValidators}>
          {(field) => (
            <field.TextInput
              inputRef={lastNameRef}
              placeholder="Enter your last name"
              returnKeyType="next"
              autoCorrect={false}
              autoComplete="family-name"
              textContentType="familyName"
              onSubmitEditing={() => usernameRef.current?.focus()}
              containerStyle={styles.stackedTextInputContainer}
              testID="screens.UserFormScreen.TextInput.LastName"
            />
          )}
        </form.AppField>
      </View>
    ) : null;

  const renderUsernameInput = () => (
    <View style={styles.itemContainer}>
      {renderHeader(
        'account-outline',
        'Username',
        'Your username must be between 4 and 20 characters.'
      )}
      <form.AppField
        name="username"
        validators={{
          onChangeAsync: async ({ value }) =>
            (await maybeValidateUsername(value, { updateAvailability: true })) || undefined,
          onChangeAsyncDebounceMs: USERNAME_VALIDATION_DEBOUNCE_MS,
          onSubmitAsync: async ({ value }) =>
            (await maybeValidateUsername(value, { updateAvailability: true })) || undefined,
        }}>
        {(field) => (
          <field.TextInput
            inputRef={usernameRef}
            placeholder="Enter a username"
            returnKeyType="next"
            autoCapitalize="none"
            autoCorrect={false}
            autoComplete="username"
            // important: trim to prevent whitespace!
            transformValue={(text) => text.trim()}
            onSubmitEditing={() => hometownRef.current?.focus()}
            left={<TextInputIcon icon="at" />}
            right={renderUsernameAvailabilityIcon(field.state.value)}
            containerStyle={styles.textInputContainer}
            testID="screens.UserFormScreen.TextInput.Username"
          />
        )}
      </form.AppField>
    </View>
  );

  const renderPhotoInput = () => (
    <View style={styles.itemContainer}>
      {renderHeader('image', 'Profile photo', 'Your profile photo will be visible to other users.')}
      <form.AppField name="profilePhoto">
        {(field) => (
          <field.PhotoInput circle emptyValue={null} containerStyle={styles.imageContainer} />
        )}
      </form.AppField>
    </View>
  );

  const renderHometownInput = () => (
    <View style={styles.itemContainer}>
      {renderHeader(
        'home-outline',
        'Residence',
        'We will notify you when people you follow have plans here.'
      )}
      <View style={styles.searchContainer}>
        <form.AppField
          name="hometown"
          validators={{
            onChange: ({ value }) => validatePlace(value) || undefined,
            onSubmit: ({ value }) => validatePlace(value) || undefined,
          }}>
          {(field) => (
            <field.PlaceAutocomplete
              types="(cities)"
              placeholder="Enter a city or town"
              inputRef={hometownRef}
              returnKeyType="next"
              containerStyle={styles.textInputContainer}
              testID="screens.UserFormScreen.GooglePlacesAutoComplete"
            />
          )}
        </form.AppField>
      </View>
    </View>
  );

  const renderPublicSwitch = () => (
    <View style={styles.itemContainer}>
      {renderHeader(
        'account-multiple',
        'Privacy',
        'With a private profile, only people you allow can view your plans.'
      )}
      <View style={styles.switchContainer}>
        <form.AppField name="isPublic">
          {(field) => (
            <>
              <Paragraph>Public</Paragraph>
              <Icon name="earth" style={styles.switchIcon} />
              <Switch
                value={!field.state.value}
                onValueChange={() => field.handleChange(!field.state.value)}
              />
              <Icon name="lock" style={styles.switchIcon} />
              <Paragraph>Private</Paragraph>
            </>
          )}
        </form.AppField>
      </View>
    </View>
  );

  return (
    <form.AppForm>
      <ScreenContainer
        scrollable
        loading={loading}
        testID="screens.UserFormScreen.Background"
        {...screenContainerProps}>
        <View style={[styles.screen, loadExistingUser ? styles.updateScreen : undefined]}>
          {title ? <PrimaryHeadline style={styles.header}>{title}</PrimaryHeadline> : null}
          {renderDisplayNameInputs()}
          {renderUsernameInput()}
          {renderHometownInput()}
          {renderPhotoInput()}
          {renderPublicSwitch()}
          <form.SubmitBigButton
            mode="contained"
            style={styles.button}
            testID="screens.UserFormScreen.Button.Continue">
            {buttonText || 'Continue'}
          </form.SubmitBigButton>
        </View>
      </ScreenContainer>
    </form.AppForm>
  );
}

const styles = StyleSheet.create({
  button: {
    marginTop: 20,
  },
  caption: {
    textAlign: 'left',
  },
  header: {
    textAlign: 'center',
  },
  imageContainer: {
    alignSelf: 'center',
    marginTop: 8,
  },
  itemContainer: {
    marginTop: 10,
  },
  screen: {
    paddingHorizontal: 20,
  },
  searchContainer: {
    flexDirection: 'row',
    marginVertical: 0,
    width: '100%',
  },
  stackedTextInputContainer: {
    marginTop: 8,
    marginVertical: 0,
  },
  switchContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 5,
  },
  switchIcon: {
    marginHorizontal: 5,
  },
  textInputContainer: {
    marginVertical: 0,
  },
  updateScreen: {
    paddingBottom: 40,
  },
});
